/* eslint-disable react-hooks/exhaustive-deps */

import { withStyles } from '@material-ui/core';
import {
  Box,
  Grid,
  Button,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TableRow,
  CardContent,
  Tooltip,
  Card,
  TableHead
} from '@mui/material';
import { compose } from 'recompose';
import { connect } from 'react-redux';
import React, { useEffect, useMemo, useState } from 'react';

import CreditsChargesCard from './creditsChargesCard.component';
import EquityDetailsCard from './equityDetailsCard.component';
import MortgagesCard from './mortgagesCard.component';
import EquityVoucherWizardComponent from './equityVoucherWizard.component';
import CrudTableComponent from 'modules/clients/details/crudTable.component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  getHomesaleInfo,
  postEquityDetails,
  unpostEquityDetails,
  unpostEquityVouchers,
  createHomesaleDispositionReport,
  getHomesaleDispositionReport
} from 'modules/authorizations/store/actions/homesale.actions';
import {
  POST_EQUITY_DETAILS_FAILURE,
  UNPOST_EQUITY_VOUCHERS_FAILURE,
  UNPOST_EQUITY_DETAILS_FAILURE,
  CREATE_HOMESALE_DISPOSITION_REPORT_FAILURE,
  GET_HOMESALE_DISPOSITION_REPORT_FAILURE
} from 'modules/authorizations/store/types/homesaleTypes';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { Permissions } from 'modules/layout/permissions';
import { hasPermission } from 'modules/layout/permissionService';
import { showToast } from 'modules/layout/layout.actions';
import SpinnerComponent from 'modules/common/spinner.component';
import { formatMoney } from 'utilities/currencyFormatter';

const styles = theme => ({
  card: {
    width: '100%',
    maxWidth: 1500,
    flexGrow: 1,
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  cardContainer: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between'
  },
  iconRow: {
    display: 'flex',
    flexDirection: 'row'
  },
  dialogIconRow: {
    display: 'flex',
    flexDirection: 'row',
    padding: '40px',
    paddingBottom: '20px'
  },
  cardDisplayContent: {
    maxWidth: '100% !important',
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    margin: '10px'
  }
});

const Equity = props => {
  const {
    details,
    equityId,
    expenseCodes,
    isReadOnly,
    enumerations,
    authId,
    equityOptions = null,
    equities = [],
    setEquityId,
    setShowAddEquity,
    exportSampleFile,
    homesaleId,
    authBanks,
    clientId,
    authFileNumber,
    equityStatuses
  } = props;

  const [showPostVoucher, setShowPostVoucher] = useState(null);
  const [isLoadingPostedDetails, setIsLoadingPostedDetails] = useState(false);

  const [showDispositionReports, setShowDispositionReports] = useState(false);
  const [dispositionReports, setDispositionReports] = useState([]);
  const [isLoadingDispositionReports, setIsLoadingDispositionReports] = useState(false);

  const [confirmation, setConfirmation] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);

  const requestConfirmation = ({ title, message, action }) => {
    setConfirmation({
      title,
      message,
      action
    });
  };

  const closeConfirmation = () => {
    if (!isConfirming) {
      setConfirmation(null);
    }
  };

  const confirmAction = async () => {
    if (!confirmation?.action) return;

    setIsConfirming(true);

    try {
      await confirmation.action();
      setConfirmation(null);
    } finally {
      setIsConfirming(false);
    }
  };

  const equityList = useMemo(() => {
    if (details?.homesale?.equities) return details.homesale.equities;
    if (equityOptions) return equityOptions;
    return equities || [];
  }, [details, equityOptions, equities]);

  const projectTransactionReferenceId = details?.homesale?.homesaleInfo?.acumaticaPostedDetailsReferenceNumber || null;
  const postedDetailsDate = details?.homesale?.homesaleInfo?.postedDetailsDate || null;
  const dispositionReportSummaries = details?.homesale?.dispositionReports || [];

  useEffect(() => {
    if (!equityId && equityList.length > 0) {
      setEquityId(equityList[equityList.length - 1].equityInfo.homesaleEquityId);
    }
  }, [equityId, equityList, setEquityId]);

  const equityInfo = useMemo(() => {
    return equityList.find(a => a.equityInfo.homesaleEquityId === equityId) || null;
  }, [equityList, equityId]);

  const formatDate = value => {
    if (!value) return '';
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('en-us');
  };

  const formatCurrency = value => {
    if (value == null || value === '') return '';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  const getNewestVoucher = vouchers => {
    if (!vouchers || vouchers.length === 0) return null;

    return vouchers.reduce((latest, current) => {
      const latestTime = new Date(latest.datePosted ?? latest.DatePosted).getTime();
      const currentTime = new Date(current.datePosted ?? current.DatePosted).getTime();
      return currentTime > latestTime ? current : latest;
    });
  };

  const getEquityVersionNumber = opt => {
    return opt.equityInfo.equityVersion ?? opt.equityInfo.version ?? '';
  };

  const getEquityStatusTitle = (opt, equityStatuses) => {
    if (!opt?.equityInfo?.equityStatusId || !equityStatuses) return '';
    return equityStatuses.find(e => e.id === opt.equityInfo.equityStatusId)?.title || '';
  };

  const getEquityLabel = (opt, equityStatuses) => {
    const newestVoucher = getNewestVoucher(opt.homesaleEquityVouchers || []);
    const postedDate = newestVoucher ? formatDate(newestVoucher.datePosted ?? newestVoucher.DatePosted) : '';
    const amountPosted = newestVoucher
      ? formatCurrency(newestVoucher.equityTotalAmountWhenPosted ?? newestVoucher.EquityTotalAmountWhenPosted)
      : '';
    const createdDate = formatDate(opt.equityInfo.addedOn);
    const status = getEquityStatusTitle(opt, equityStatuses);
    const equityVersionDesc = opt.equityInfo.equityVersionStatusDescription || '';

    return (
      <Box>
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          Type: {status || '—'} ({getEquityVersionNumber(opt) ?? 1})
        </Typography>
        <Typography variant="caption" display="block">
          Created: {createdDate || '—'}
        </Typography>
        <Typography variant="caption" display="block">
          Posted: {postedDate || '—'}
        </Typography>
        <Typography variant="caption" display="block">
          Amt Posted: {amountPosted || '—'}
        </Typography>
        <Typography variant="caption" display="block">
          Status: {equityVersionDesc || '—'}
        </Typography>
      </Box>
    );
  };

  const [pOpen, setPOpen] = useState(true);
  const [sOpen, setSOpen] = useState(true);

  const homesaleEquityVouchers = equityInfo && equityInfo.homesaleEquityVouchers ? equityInfo.homesaleEquityVouchers : [];
  const pEquities = equityList.filter(x => (x.equityInfo.equityRecordType || '').toUpperCase() === 'P');
  const sEquities = equityList.filter(x => (x.equityInfo.equityRecordType || '').toUpperCase() === 'S');

  const canPostEquity = equityInfo ? equityInfo.canPostEquity : false;
  const canUnpostEquity = equityInfo ? equityInfo.canUnpostEquity : false;
  const canPostEquityVouchers = hasPermission(Permissions.File.Homesale.Equity.Post);

  //TODO - Rules for when we are allowed to post
  const canPostDetails = equityInfo ? equityInfo.canPostEquityDetails : false;
  const canUnpostDetails = equityInfo ? equityInfo.canUnpostEquityDetails : false;
  const canPostEquityDetails = hasPermission(Permissions.File.Homesale.Equity.PostDetail);

  const version = equityInfo ? getEquityVersionNumber(equityInfo) : 1;

  const postEquityDetails = async () => {
    setIsLoadingPostedDetails(true);
    const resp = await props.postEquityDetails(authId, homesaleId);
    if (resp.type === POST_EQUITY_DETAILS_FAILURE) {
      props.showToast('Failed to post equity details, please try again', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
    } else {
      await props.getHomesaleInfo(authId, homesaleId);
      props.showToast('Equity details posted successfully', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS
      });
    }
    setIsLoadingPostedDetails(false);
  };

  const unpostEquityDetails = async () => {
    setIsLoadingPostedDetails(true);
    const resp = await props.unpostEquityDetails(authId, homesaleId);
    if (resp.type === UNPOST_EQUITY_DETAILS_FAILURE) {
      props.showToast('Failed to unpost equity details, please try again', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
    } else {
      await props.getHomesaleInfo(authId, homesaleId);
      props.showToast('Equity details unposted successfully', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS
      });
    }
    setIsLoadingPostedDetails(false);
  };

  const unpostEquity = async () => {
    setIsLoadingPostedDetails(true);
    const resp = await props.unpostEquityVouchers(authId, homesaleId, equityId);
    if (resp.type === UNPOST_EQUITY_VOUCHERS_FAILURE) {
      props.showToast('Failed to unpost equity details, please try again', {
        severity: TOAST_MESSAGE_SEVERITY_ERROR
      });
    } else {
      await props.getHomesaleInfo(authId, homesaleId);
      props.showToast('Equity details unposted successfully', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS
      });
    }
    setIsLoadingPostedDetails(false);
  };

  const getDispositionReportId = report => report.dispositionReportId ?? report.DispositionReportId;

  const getDispositionReportVersion = report => report.version ?? report.Version ?? '';

  const getDispositionReportAddedOn = report => report.addedOn ?? report.AddedOn ?? null;

  const openPdfResponse = response => {
    const pdfBlob = response instanceof Blob ? response : new Blob([response], { type: 'application/pdf' });

    const pdfUrl = URL.createObjectURL(pdfBlob);
    window.open(pdfUrl, '_blank', 'noopener,noreferrer');

    setTimeout(() => {
      URL.revokeObjectURL(pdfUrl);
    }, 60000);
  };

  const viewDispositionReport = async dispositionReportId => {
    if (!dispositionReportId) return;

    setIsLoadingDispositionReports(true);

    try {
      const response = await props.getHomesaleDispositionReport(authId, homesaleId, dispositionReportId);

      if (response.type === GET_HOMESALE_DISPOSITION_REPORT_FAILURE || !response.response) {
        props.showToast('Failed to open disposition report', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR
        });

        return;
      }

      openPdfResponse(response.response);
      setShowDispositionReports(false);
    } finally {
      setIsLoadingDispositionReports(false);
    }
  };

  const loadDispositionReports = async () => {
    setIsLoadingDispositionReports(true);

    try {
      if (!Array.isArray(dispositionReportSummaries) || dispositionReportSummaries.length === 0) {
        props.showToast('No disposition reports were found', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR
        });

        return;
      }

      const orderedReports = [...dispositionReportSummaries].sort(
        (first, second) => Number(getDispositionReportVersion(second)) - Number(getDispositionReportVersion(first))
      );

      setDispositionReports(orderedReports);
      setShowDispositionReports(true);
    } finally {
      setIsLoadingDispositionReports(false);
    }
  };

  const createDispositionReport = async () => {
    setIsLoadingPostedDetails(true);

    try {
      const response = await props.createHomesaleDispositionReport(authId, homesaleId);

      if (response.type === CREATE_HOMESALE_DISPOSITION_REPORT_FAILURE || !response.response) {
        props.showToast('Failed to create disposition report, please try again', {
          severity: TOAST_MESSAGE_SEVERITY_ERROR
        });

        return;
      }

      await props.getHomesaleInfo(authId, homesaleId);

      openPdfResponse(response.response);

      props.showToast('Disposition report created successfully', {
        severity: TOAST_MESSAGE_SEVERITY_SUCCESS
      });
    } finally {
      setIsLoadingPostedDetails(false);
    }
  };

  const smallButtonSx = {
    minWidth: 'unset',
    height: '34px',
    fontSize: '12px',
    padding: '0 12px',
    whiteSpace: 'nowrap'
  };

  const createRows = postedVouchers => {
    if (!postedVouchers || Array.isArray(postedVouchers) === false) return [];

    let rows = [];
    for (var i = 0; i < postedVouchers.length; i++) {
      let pv = postedVouchers[i];
      for (let y = 0; y < pv.voucherSummaries.length; y++) {
        let vs = pv.voucherSummaries[y];
        rows.push({
          voucherId: vs.voucherId,
          datePosted: pv.datePosted,
          postedAmount: vs.postedAmount,
          voucherStatus: vs.voucherStatus
        });
      }
    }

    return rows.sort((a, b) => new Date(b.datePosted) - new Date(a.datePosted));
  };

  const adjustedColumns = [
    { accessorKey: 'voucherId', header: 'Voucher Id', type: 'number', required: true, size: 75 },
    {
      accessorKey: 'datePosted',
      header: 'Date Posted',
      type: 'date',
      size: 75,
      Cell: ({ cell }) => (cell.getValue() ? new Date(cell.getValue()).toLocaleDateString('en-us') : '-')
    },
    {
      accessorKey: 'postedAmount',
      header: 'Posted Amount',
      type: 'money',
      size: 75,
      Cell: ({ cell }) => (cell.getValue() ? formatMoney(cell.getValue()) : '-')
    },
    { accessorKey: 'voucherStatus', header: 'Status', type: 'text', size: 75 }
  ];

  return (
    <Box sx={{ padding: '8px', marginLeft: '-5px' }}>
      {(!isReadOnly || (canPostEquityVouchers || canPostEquityDetails)) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 2,
            flexWrap: 'wrap',
            marginTop: '-10px'
          }}
        >
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button
              color="secondary"
              variant="contained"
              sx={smallButtonSx}
              onClick={() => setShowAddEquity(true)}
              disabled={isReadOnly || isLoadingPostedDetails}
            >
              Add Equity
            </Button>
          </Box>
          {canUnpostDetails && (
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ marginBottom: '8px' }}>
                Details Posted Date - {new Date(postedDetailsDate).toLocaleDateString('en-us')}
              </Typography>
            </Box>
          )}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {isLoadingPostedDetails && <SpinnerComponent />}
            {canUnpostDetails && (
              <>
                <Button
                  color="secondary"
                  variant="contained"
                  sx={smallButtonSx}
                  onClick={() =>
                    requestConfirmation({
                      title: 'Create Disposition Report',
                      message: 'Are you sure you want to create a new disposition report?',
                      action: createDispositionReport
                    })
                  }
                  disabled={isReadOnly || isLoadingPostedDetails || isConfirming}
                >
                  Create Disposition Report
                </Button>
                {dispositionReportSummaries && dispositionReportSummaries.length > 0 && (
                  <Button
                    color="secondary"
                    variant="contained"
                    sx={smallButtonSx}
                    onClick={loadDispositionReports}
                    disabled={isReadOnly || isLoadingPostedDetails || isLoadingDispositionReports}
                  >
                    View Disposition Report
                  </Button>
                )}
              </>
            )}
            {equityInfo && (
              <Button
                color="secondary"
                variant="contained"
                sx={smallButtonSx}
                onClick={() =>
                  requestConfirmation({
                    title: 'Export Report',
                    message: 'Are you sure you want to export this report?',
                    action: exportSampleFile
                  })
                }
                disabled={isReadOnly || isLoadingPostedDetails || isConfirming}
              >
                Export Report
              </Button>
            )}

            {equityInfo && canPostEquity && !canUnpostEquity && (
              <Button
                color="secondary"
                variant="contained"
                sx={smallButtonSx}
                onClick={() => setShowPostVoucher(true)}
                disabled={!canPostEquity || !canPostEquityVouchers || isLoadingPostedDetails}
              >
                Post Equity
              </Button>
            )}

            {equityInfo && canUnpostEquity && !canPostEquity && (
              <Button
                color="secondary"
                variant="contained"
                sx={smallButtonSx}
                onClick={() =>
                  requestConfirmation({
                    title: 'Unpost Equity',
                    message: 'Are you sure you want to unpost this equity?',
                    action: unpostEquity
                  })
                }
                disabled={!canUnpostEquity || isLoadingPostedDetails || isConfirming}
              >
                Unpost Equity
              </Button>
            )}

            {equityInfo && canPostDetails && !canUnpostDetails && (
              <Button
                color="secondary"
                variant="contained"
                sx={smallButtonSx}
                onClick={() =>
                  requestConfirmation({
                    title: 'Post Equity Details',
                    message: 'Are you sure you want to post these equity details?',
                    action: postEquityDetails
                  })
                }
                disabled={!canPostEquityDetails || isLoadingPostedDetails || isConfirming}
              >
                Post Equity Details
              </Button>
            )}

            {equityInfo && canUnpostDetails && !canPostDetails && (
              <Button
                color="secondary"
                variant="contained"
                sx={smallButtonSx}
                onClick={() =>
                  requestConfirmation({
                    title: 'Unpost Equity Details',
                    message: 'Are you sure you want to unpost these equity details?.',
                    action: unpostEquityDetails
                  })
                }
                disabled={!canPostEquityDetails || isLoadingPostedDetails || isConfirming}
              >
                Unpost Equity Details
              </Button>
            )}
          </Box>
        </Box>
      )}
      <Grid container spacing={2} sx={{ marginTop: '5px' }}>
        <Grid item xs={12} md={2} xl={2}>
          <Paper sx={{ padding: '12px' }}>
            <Typography variant="h6" sx={{ marginBottom: '8px' }}>
              Equities
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {pEquities.length > 0 && (
                    <>
                      <TableRow
                        hover
                        onClick={() => setPOpen(prev => !prev)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: '#f5f5f5',
                          '& td': { py: 1.25 }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon icon={pOpen ? ['fas', 'chevron-down'] : ['fas', 'chevron-right']} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              P Records ({pEquities.length})
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {pOpen &&
                        pEquities.map(opt => {
                          const itemId = opt.equityInfo.homesaleEquityId;
                          const selected = equityId === itemId;

                          return (
                            <TableRow
                              key={itemId}
                              hover
                              selected={selected}
                              onClick={() => setEquityId(itemId)}
                              sx={{
                                cursor: 'pointer',
                                '& td': { py: 1.5 },
                                '&.Mui-selected': {
                                  backgroundColor: '#e3f2fd'
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: '#d7ebfc'
                                }
                              }}
                            >
                              <TableCell sx={{ pl: 2 }}>{getEquityLabel(opt, equityStatuses)}</TableCell>
                            </TableRow>
                          );
                        })}
                    </>
                  )}

                  {sEquities.length > 0 && (
                    <>
                      <TableRow
                        hover
                        onClick={() => setSOpen(prev => !prev)}
                        sx={{
                          cursor: 'pointer',
                          backgroundColor: '#f5f5f5',
                          '& td': { py: 1.25 }
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <FontAwesomeIcon icon={sOpen ? ['fas', 'chevron-down'] : ['fas', 'chevron-right']} />
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                              S Records ({sEquities.length})
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>

                      {sOpen &&
                        sEquities.map(opt => {
                          const itemId = opt.equityInfo.homesaleEquityId;
                          const selected = equityId === itemId;

                          return (
                            <TableRow
                              key={itemId}
                              hover
                              selected={selected}
                              onClick={() => setEquityId(itemId)}
                              sx={{
                                cursor: 'pointer',
                                '& td': { py: 1.5 },
                                '&.Mui-selected': {
                                  backgroundColor: '#e3f2fd'
                                },
                                '&.Mui-selected:hover': {
                                  backgroundColor: '#d7ebfc'
                                }
                              }}
                            >
                              <TableCell sx={{ pl: 2 }}>{getEquityLabel(opt, equityStatuses)}</TableCell>
                            </TableRow>
                          );
                        })}
                    </>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {equityList.length === 0 && (
              <Box sx={{ paddingTop: '8px' }}>
                <Typography variant="body2">No equities available.</Typography>
              </Box>
            )}
          </Paper>
        </Grid>

        <Grid item xs={12} md={10} xl={10}>
          {equityInfo == null ? (
            <Box sx={{ padding: '20px', fontSize: '20px' }}>
              <b>No Equity selected. Create a new one to get started.</b>
            </Box>
          ) : (
            <Grid container rowSpacing={2} columnSpacing={{ xs: 1, sm: 2, md: 2 }}>
              <Grid item xs={12} xl={7}>
                <CreditsChargesCard
                  equity={equityInfo}
                  expenseCodes={expenseCodes}
                  isReadOnly={isReadOnly || isLoadingPostedDetails}
                  fileId={authId}
                  projectTransactionReferenceId={projectTransactionReferenceId}
                  postedDetailsDate={postedDetailsDate}
                />
                <Box sx={{ paddingTop: '16px' }}>
                  <MortgagesCard
                    equity={equityInfo}
                    isReadOnly={isReadOnly || isLoadingPostedDetails}
                    enumerations={enumerations}
                    fileId={authId}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} xl={5}>
                <EquityDetailsCard
                  equity={equityInfo}
                  isReadOnly={isReadOnly || isLoadingPostedDetails}
                  version={version}
                  fileId={authId}
                  creditCharges={equityInfo ? equityInfo.creditCharges : []}
                />
                <Box sx={{ paddingTop: '16px' }}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', flexDirection: 'row' }}>
                        <Tooltip title={'Posted Equity'} key={'postedEquity'} placement="right">
                          <FontAwesomeIcon icon={['fas', 'calendar-days']} size="2x" />
                        </Tooltip>
                        <Typography variant="h5" sx={{ paddingLeft: '20px', fontSize: '1.3125rem', fontWeight: 'bold' }}>
                          Posted Equity
                        </Typography>
                      </Box>
                      <CrudTableComponent
                        showBasicTable={true}
                        editingMode={'modal'} // Can be changed to 'row' to edit inline
                        columns={adjustedColumns}
                        rows={createRows(homesaleEquityVouchers)}
                        // onRowClick={row => {
                        //   const feeRecordId = row.original.feeRecordId;
                        //   if (feeRecordId) {
                        //     history.push(`/fees/${feeRecordId}`);
                        //   }
                        // }}
                        hideEdit={true}
                        hideAdd={true}
                      />
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Dialog open={showDispositionReports} onClose={() => setShowDispositionReports(false)} fullWidth maxWidth="sm">
        <Box sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Disposition Reports
          </Typography>

          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Version</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell align="right">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {dispositionReports.map(report => {
                  const reportId = getDispositionReportId(report);
                  const version = getDispositionReportVersion(report);
                  const addedOn = getDispositionReportAddedOn(report);

                  return (
                    <TableRow key={reportId}>
                      <TableCell>{version ? `Version ${version}` : '—'}</TableCell>

                      <TableCell>{addedOn ? new Date(addedOn).toLocaleString('en-US') : '—'}</TableCell>

                      <TableCell align="right">
                        <Button
                          color="secondary"
                          variant="contained"
                          size="small"
                          onClick={() => viewDispositionReport(reportId)}
                          disabled={isLoadingDispositionReports}
                        >
                          Open
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 3
            }}
          >
            <Button color="secondary" onClick={() => setShowDispositionReports(false)}>
              Cancel
            </Button>
          </Box>
        </Box>
      </Dialog>

      <Dialog open={showPostVoucher} fullWidth={true} maxWidth={'lg'}>
        <EquityVoucherWizardComponent
          equityId={equityId}
          equityInfo={equityInfo}
          authFileId={authId}
          homesaleId={homesaleId}
          setShowPostVoucher={setShowPostVoucher}
          authBanks={authBanks}
          clientId={clientId}
          isReadOnly={isReadOnly || isLoadingPostedDetails}
          authFileNumber={authFileNumber}
        />
      </Dialog>

      <Dialog open={Boolean(confirmation)} onClose={closeConfirmation} fullWidth maxWidth="xs">
        <DialogTitle>{confirmation?.title}</DialogTitle>

        <DialogContent>
          <DialogContentText>{confirmation?.message}</DialogContentText>
        </DialogContent>

        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button onClick={closeConfirmation} color="inherit" disabled={isConfirming}>
            Cancel
          </Button>

          <Button onClick={confirmAction} color="secondary" variant="contained" disabled={isConfirming} autoFocus>
            {isConfirming ? 'Working...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

const mapStateToProps = state => {
  const { expenseCodes } = state.authorizations;
  return {
    expenseCodes
  };
};

export default compose(
  withStyles(styles),
  connect(mapStateToProps, {
    postEquityDetails,
    getHomesaleInfo,
    showToast,
    unpostEquityVouchers,
    unpostEquityDetails,
    createHomesaleDispositionReport,
    getHomesaleDispositionReport
  })
)(Equity);
